import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { CameraLiveViewIcon } from '@hakimo-ui/hakimo/ui-elements';
import {
  PAGE_LAYOUT_LIST_WIDTH,
  PageLayout,
} from '@hakimo-ui/hakimo/ui-layout';
import {
  useFullscreen,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import CameraLimitModal from './CameraLimitModal';
import MultiCamDetails from './details/MultiCamDetails';
import MultiCamDragOverlay from './list/MultiCamDragOverlay';
import MultiCamSelectionPanel from './list/MultiCamSelectionPanel';
import {
  DROP_ZONE_ID,
  DragItem,
  cameraLimitAlertOpenAtom,
  fullScreenTargetAtom,
  tabsAtom,
} from './state';
import useMultiCamSelection from './useMultiCamSelection';

export function MultiCamRoot() {
  const [cameraLimitAlertOpen, setCameraLimitAlertOpen] = useAtom(
    cameraLimitAlertOpenAtom
  );
  const setFullScreenTarget = useSetAtom(fullScreenTargetAtom);
  const tabs = useAtomValue(tabsAtom);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { isFullScreen, toggleFullScreen } = useFullscreen(rootRef);
  const { commit } = useMultiCamSelection();
  const [dragItem, setDragItem] = useState<DragItem | null>(null);
  const [listExpanded, setListExpanded] = useState(false);
  const listCollapsed = isFullScreen && tabs.length > 0 && !listExpanded;

  useEffect(() => {
    if (!isFullScreen) setListExpanded(false);
    setFullScreenTarget(isFullScreen ? rootRef : null);
  }, [isFullScreen, setFullScreenTarget]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const onDragStart = (event: DragStartEvent) => {
    setDragItem(event.active.data.current as DragItem);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragItem(null);
    if (!over || over.id !== DROP_ZONE_ID) return;
    const item = active.data.current as DragItem | undefined;
    if (!item) return;
    if (item.type === 'camera') {
      commit([item.camera], []);
    } else {
      commit([], [item.group]);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div ref={rootRef} className="relative h-full w-full">
        <PageLayout
          variant="split"
          title="Live Views"
          icon={CameraLiveViewIcon}
          listCollapsed={listCollapsed}
        >
          <PageLayout.List>
            <MultiCamSelectionPanel />
          </PageLayout.List>
          <PageLayout.Details>
            <MultiCamDetails
              isFullScreen={isFullScreen}
              onToggleFullScreen={toggleFullScreen}
            />
          </PageLayout.Details>
        </PageLayout>
        {isFullScreen && tabs.length > 0 && (
          <button
            type="button"
            title={listCollapsed ? 'Expand list' : 'Collapse list'}
            onClick={() => setListExpanded((prev) => !prev)}
            style={{ left: listCollapsed ? 0 : PAGE_LAYOUT_LIST_WIDTH }}
            className="border-onlight-line-2 dark:border-ondark-line-2 bg-onlight-bg-1 dark:bg-ondark-bg-1 text-onlight-text-2 dark:text-ondark-text-1 hover:text-primary-500 hover:border-primary-500 absolute top-1/2 z-0 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border shadow-lg transition-[left,color] duration-300 ease-in-out"
          >
            {listCollapsed ? (
              <ChevronDoubleRightIcon className="h-6 w-6 translate-x-2" />
            ) : (
              <ChevronDoubleLeftIcon className="h-6 w-6 translate-x-2" />
            )}
          </button>
        )}
        <MultiCamDragOverlay item={dragItem} />
      </div>
      <CameraLimitModal
        open={cameraLimitAlertOpen}
        onClose={() => setCameraLimitAlertOpen(false)}
        portalTarget={isFullScreen ? rootRef : undefined}
      />
    </DndContext>
  );
}

export default withAuthz(withErrorBoundary(MultiCamRoot), ['camera:view']);
